import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import BlogCard from '@/components/BlogCard';
import BlogSeoContent from '@/components/BlogSeoContent';
import Seo from '@/components/Seo';
import { Search, Loader2 } from 'lucide-react';

export default function Blog() {
  return (
    <>
      <Seo
        title="Garage Door Repair Blog | Tips & Guides | Mike's Garage Door Repair"
        description="Expert garage door repair tips, maintenance guides, and how-to articles for Cypress, TX homeowners — covering springs, openers, sensors, and emergency repairs from Mike's team."
        path="/blog"
      />
    <BlogInner />
    </>
  );
}

function BlogInner() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  const categories = ['All', ...Array.from(new Set(posts.map((p) => p.category).filter(Boolean)))];

  useEffect(() => {
    const load = async () => {
      try {
        const data = await base44.entities.Article.list('-published_at', 50);
        setPosts(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = posts.filter((post) => {
    const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
    const matchesSearch = !search ||
      post.title.toLowerCase().includes(search.toLowerCase()) ||
      (post.subtitle || post.meta_description || '').toLowerCase().includes(search.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <>
      <Navbar />
      <main className="pt-16">
        {/* Header */}
        <section className="bg-background border-b border-border tech-grid">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
            <div className="max-w-3xl">
              <div className="font-mono text-xs text-primary uppercase tracking-wider stencil mb-4">
                / Mike's Garage Door Blog
              </div>
              <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-foreground text-balance leading-[0.95]">
                Garage Door Repair <span className="text-primary">Tips & Guides</span>
              </h1>
              <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
                Expert advice on garage door springs, openers, maintenance, and emergency repairs —
                written for Cypress, TX homeowners by Mike himself.
              </p>
            </div>
          </div>
        </section>

        {/* Filters + Search */}
        <section className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center justify-between">
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-4 py-2 font-heading font-bold text-xs uppercase tracking-wider transition-all ${
                      activeCategory === cat
                        ? 'bg-primary text-background'
                        : 'bg-background border border-border text-muted-foreground hover:text-primary hover:border-primary/50'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              <div className="relative lg:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full bg-background border border-border pl-10 pr-4 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none transition-colors"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Posts grid */}
        <section className="bg-background py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-24">
                <p className="text-muted-foreground text-lg">No articles found. Try a different search or category.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map((post) => (
                  <BlogCard key={post.id} post={post} />
                ))}
              </div>
            )}
          </div>
        </section>
        <BlogSeoContent />
      </main>
      <Footer />
    </>
  );
}